import { useState } from "react"

import { type ApiError, ProgramsService } from "@/client"
import {
  type ProgramBulkEnrollResult,
  uploadProgramEnrollmentsCsv,
} from "@/client/bulkUpload"
import type { ProgramEnrollmentsPublic } from "@/client/types.gen"
import { EnrollUsersModal } from "@/components/Programs/EnrollUsersModal"
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import {
  Alert,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  Input,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FaSearch, FaTrash } from "react-icons/fa"

import useCustomToast from "@/hooks/useCustomToast"
import { useDebounce } from "@/hooks/useDebounce"
import { handleError } from "@/utils"

interface ProgramEnrollmentsProps {
  programId: string
  programName: string
}

function BulkEnrollCsvDialog({
  programId,
  programName,
}: ProgramEnrollmentsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ProgramBulkEnrollResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const resetState = () => {
    setFile(null)
    setPreview(null)
  }

  const handlePreview = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      const result = await uploadProgramEnrollmentsCsv({
        programId,
        file,
        previewOnly: true,
        createMissingUsers: true,
      })
      setPreview(result)
    } catch (err) {
      handleError(err as ApiError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      const result = await uploadProgramEnrollmentsCsv({
        programId,
        file,
        previewOnly: false,
        createMissingUsers: true,
      })
      setPreview(result)
      showSuccessToast("Bulk enrollment completed")
      queryClient.invalidateQueries({
        queryKey: ["program-enrollments", programId],
      })
      queryClient.invalidateQueries({ queryKey: ["program", programId] })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    } catch (err) {
      handleError(err as ApiError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownloadTemplate = () => {
    const csv = [
      "email,full_name",
      "student1@example.com,Student One",
      "student2@example.com,Student Two",
    ].join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "program-enrollment-template.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <DialogRoot
      placement="center"
      size={{ base: "xs", md: "lg" }}
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (!open) {
          resetState()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">Bulk enroll via CSV</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk enroll students in {programName}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text color="fg.muted">
              Upload a CSV with <strong>email</strong> (required) and
              <strong>full_name</strong> (optional). New users will be created
              as students and enrolled in this program.
            </Text>
            <Button
              alignSelf="flex-start"
              size="sm"
              variant="subtle"
              onClick={handleDownloadTemplate}
            >
              Download sample CSV
            </Button>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const selected = e.target.files?.[0] ?? null
                setFile(selected)
                setPreview(null)
              }}
            />
            {preview && (
              <VStack align="stretch" gap={2} mt={2}>
                <Text fontWeight="medium">Preview summary</Text>
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="fg.muted">
                    Valid rows: {preview.valid_rows} / {preview.total_rows}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Created users: {preview.created}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Added to org: {preview.added_to_org}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Enrolled: {preview.enrolled}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Already enrolled: {preview.already_enrolled}
                  </Text>
                </HStack>
                {preview.errors?.length ? (
                  <VStack align="stretch" gap={1} maxH="180px" overflowY="auto">
                    <Text fontSize="sm" color="fg.error">
                      Errors ({preview.errors.length})
                    </Text>
                    {preview.errors.map((err) => (
                      <Text
                        key={`${err.row}-${err.email ?? err.row}`}
                        fontSize="sm"
                        color="fg.error"
                      >
                        Row {err.row}: {err.error}
                        {err.email ? ` (${err.email})` : ""}
                      </Text>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="fg.success">
                    No validation errors detected.
                  </Text>
                )}
              </VStack>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter gap={2} flexWrap="wrap">
          <DialogActionTrigger asChild>
            <Button
              variant="subtle"
              colorPalette="gray"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!file || isSubmitting}
            loading={isSubmitting}
          >
            Preview
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleUpload}
            disabled={!file || isSubmitting}
            loading={isSubmitting}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

export function ProgramEnrollments({
  programId,
  programName,
}: ProgramEnrollmentsProps) {
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const debouncedSearch = useDebounce(searchTerm, 300)

  const { data, isLoading, isError, error } =
    useQuery<ProgramEnrollmentsPublic>({
      queryKey: [
        "program-enrollments",
        programId,
        currentPage,
        debouncedSearch,
      ],
      queryFn: () =>
        ProgramsService.listEnrollments({
          programId,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          search: debouncedSearch || undefined,
        }),
    })

  const unenrollMutation = useMutation({
    mutationFn: (userId: string) =>
      ProgramsService.unenrollUser({
        programId,
        userId,
      }),
    onMutate: (userId: string) => {
      setPendingUserId(userId)
    },
    onSuccess: () => {
      showSuccessToast("User unenrolled successfully")
      queryClient.invalidateQueries({
        queryKey: ["program-enrollments", programId],
      })
      queryClient.invalidateQueries({ queryKey: ["program", programId] })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
    onError: (apiError: ApiError) => {
      handleError(apiError)
    },
    onSettled: () => {
      setPendingUserId(null)
    },
  })

  const enrollments = data?.data ?? []
  const enrollmentCount = data?.count ?? 0
  const totalPages = Math.ceil(enrollmentCount / pageSize)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset to first page on search
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={4}
      bg="bg.surface"
      data-testid="program-enrollments-panel"
    >
      <Heading size="md" mb={4}>
        Enrolled Users ({enrollmentCount})
      </Heading>

      <HStack mb={4} gap={4}>
        <Box flex={1} maxW="md">
          <InputGroup startElement={<FaSearch />}>
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              data-testid="program-enrollments-search"
            />
          </InputGroup>
        </Box>
        <BulkEnrollCsvDialog programId={programId} programName={programName} />
        <EnrollUsersModal programId={programId} programName={programName} />
      </HStack>
      {isLoading ? (
        <Text color="fg.muted">Loading enrollments...</Text>
      ) : isError ? (
        <Alert.Root status="error">
          <Alert.Title>Unable to load enrollments</Alert.Title>
          <Alert.Description>
            {(error as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      ) : enrollments.length === 0 ? (
        <Box
          borderWidth="1px"
          borderRadius="md"
          py={6}
          px={4}
          textAlign="center"
        >
          <Text color="fg.muted">
            No users have been enrolled in this program yet.
          </Text>
        </Box>
      ) : (
        <>
          {/* Desktop */}
          <Box display={{ base: "none", md: "block" }}>
            <Table.Root data-testid="program-enrollments-table">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Email</Table.ColumnHeader>
                  <Table.ColumnHeader>Enrolled At</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {enrollments.map((enrollment) => (
                  <Table.Row
                    key={enrollment.id}
                    data-testid="program-enrollment-row"
                    data-user-id={enrollment.user_id}
                  >
                    <Table.Cell>
                      {enrollment.full_name || enrollment.email}
                    </Table.Cell>
                    <Table.Cell>{enrollment.email}</Table.Cell>
                    <Table.Cell>
                      {new Date(enrollment.enrolled_at).toLocaleDateString()}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        gap={2}
                        loading={
                          unenrollMutation.isPending &&
                          pendingUserId === enrollment.user_id
                        }
                        onClick={() =>
                          unenrollMutation.mutate(enrollment.user_id)
                        }
                      >
                        <FaTrash />
                        Unenroll
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile cards */}
          <VStack
            display={{ base: "flex", md: "none" }}
            gap={3}
            align="stretch"
          >
            {enrollments.map((enrollment) => (
              <Card.Root
                key={enrollment.id}
                data-testid="program-enrollment-row"
                data-user-id={enrollment.user_id}
              >
                <Card.Body>
                  <Text fontWeight="medium">
                    {enrollment.full_name || enrollment.email}
                  </Text>
                  {enrollment.full_name && (
                    <Text fontSize="sm" color="fg.muted">
                      {enrollment.email}
                    </Text>
                  )}
                  <Text fontSize="sm" color="fg.muted" mt={1}>
                    Enrolled{" "}
                    {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </Text>
                  <Flex justify="flex-end" mt={3}>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorPalette="red"
                      gap={2}
                      loading={
                        unenrollMutation.isPending &&
                        pendingUserId === enrollment.user_id
                      }
                      onClick={() =>
                        unenrollMutation.mutate(enrollment.user_id)
                      }
                    >
                      <FaTrash />
                      Unenroll
                    </Button>
                  </Flex>
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box mt={4}>
          <PaginationRoot
            count={enrollmentCount}
            pageSize={pageSize}
            page={currentPage}
            onPageChange={(details) => setCurrentPage(details.page)}
          >
            <HStack justify="center" wrap="wrap" gap={2}>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </HStack>
          </PaginationRoot>
        </Box>
      )}
    </Box>
  )
}
